import type {TypographyWeights} from '@gwc/core'
import type {SlotSize} from '@logistics-frontend/blocks'

import {ActionConfig} from '../actions/types'

export enum WidgetType {
  ACTION_BUTTON = 'action_button',
  ADDRESS = 'address',
  DIVIDER = 'divider',
  FORM = 'form',
  HEADER = 'header',
  ICON_BUTTON = 'icon_button',
  ICON_SPOT = 'icon_spot',
  IMAGE = 'image',
  NAV = 'nav',
  PAYMENT_METHOD = 'payment_method',
  RADIO = 'radio',
  SEGMENT = 'segment',
  SLOT = 'slot',
  TAG_LINE = 'tag_line',
  TEXT = 'text',
}

export type BaseWidgetConfig = {
  widget_id: string
  type: WidgetType
}

export type Image = {image_tag: string}

type ActionButtonStyle = {
  slot: {
    align: string
    title: {
      items: Array<WidgetConfig>
    }
  }
  style: 'main'
  enabled: boolean
}

export type ActionButtonWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.ACTION_BUTTON
  button_size: SlotSize
  normal_style: ActionButtonStyle
  loading_style: ActionButtonStyle
  action?: ActionConfig
  metrica_label?: string
}

export type AddressWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.ADDRESS
  route_point_index: number
  address_position: 'title' | 'subtitle'
  selected_description: {
    items: Array<TextWidgetConfig>
  }
  unselected_description: {
    items: Array<TextWidgetConfig>
  }
  address_style?: {
    size: number
    color: 'textMain'
  } // TextParameters
  // flow?: AddressFlow
  selected_lead?: IconSpotWidgetConfig
  unselected_lead?: IconSpotWidgetConfig
  trail?: NavWidgetConfig
  metrica_label?: string
}

type DividerBaseWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.DIVIDER
}

type DividerSpacerWidgetConfig = DividerBaseWidgetConfig & {
  style: 'spacer'
  spacer_height: number
}

type DividerLineMarginWidgetConfig = BaseWidgetConfig & {
  style: 'line_margin'
}

type DividerLineSlotMarginWidgetConfig = BaseWidgetConfig & {
  style: 'line_slot_margin'
}

export type DividerWidgetConfig =
  | DividerSpacerWidgetConfig
  | DividerLineMarginWidgetConfig
  | DividerLineSlotMarginWidgetConfig

export type FormWidgetConfig = Omit<BaseWidgetConfig, 'widget_id'> & {
  type: WidgetType.FORM
  header_section?: {
    widgets: Array<WidgetConfig>
  }
  sections: Array<{
    widgets: Array<WidgetConfig>
  }>
  bottom_section?: {
    widgets: Array<WidgetConfig>
  }
  metrica_label?: string
}

export type HeaderWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.HEADER
  image: Image
  title: {
    items: Array<TextWidgetConfig>
  }
  subtitle_style: {
    size: number
    color: string
  }
  trail?: IconButtonWidgetConfig
}

export type IconButtonWidgetConfig = Omit<BaseWidgetConfig, 'widget_id'> & {
  type: WidgetType.ICON_BUTTON
  image: Image
  accessibility_label: string
  action: {
    type: string
  }
}

export type IconSpotWidgetConfig = Omit<BaseWidgetConfig, 'widget_id'> & {
  type: WidgetType.ICON_SPOT
  icon_spot: {
    icon: {
      image_tag: string
      tint_color: 'text' | 'textMinor'
    }
    background_color: 'controlMinor'
  }
}

export type ImageWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.IMAGE
  background_color: string
  height: number
  text: {
    items: Array<TextWidgetConfig>
  }
  image: Image
}

export type NavWidgetConfig = Omit<BaseWidgetConfig, 'widget_id'> & {
  type: WidgetType.NAV
}

export type PaymentMethodWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.PAYMENT_METHOD
  title_style: {
    size?: number
    color?: string
  }
  additional_body: {
    align: 'trailing'
    title: {
      items: Array<TextWidgetConfig>
    }
  }
  available_payment_types: Array<'card' | 'cargocorp' | 'cash' | 'corp'>
  metrica_label?: string
}

export type RadioWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.RADIO
  enabled: boolean
  key: string
  value: string
  action?: ActionConfig
  metrica_label?: string
}

export type SegmentWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.SEGMENT
  action: {
    form_state_key: string
    options: Array<{
      selected_text: {
        title: {
          items: Array<TextWidgetConfig>
        }
      }
      unselected_text: {
        title: {
          items: Array<TextWidgetConfig>
        }
      }
      value: number
      default: boolean
      is_available: boolean
      accessibility_info: {
        is_header: boolean
        content_description: string
      }
    }>
    metrica_label?: string
  }
}

export type SlotWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.SLOT
  // accessibility_info?: {is_header: boolean}
  slot: {
    body1: {align: 'leading' | 'trailing' | 'center'; title: {items: Array<TextWidgetConfig>}}
    trail1?: WidgetConfig
  }
}

type Tag = {
  metrica_label?: string
  text: string
  icon?: Image
  action: {promotion_id: string; type: string}
}

export type TagLineWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.TAG_LINE
  tags: Array<Tag>
}

export type TextWidgetConfig = Omit<BaseWidgetConfig, 'widget_id'> & {
  type: WidgetType.TEXT
  text: string
  font_size: number
  font_weight?: TypographyWeights
  color: string
}

export type WidgetConfig =
  | ActionButtonWidgetConfig
  | AddressWidgetConfig
  | DividerWidgetConfig
  | FormWidgetConfig
  | HeaderWidgetConfig
  | IconButtonWidgetConfig
  | IconSpotWidgetConfig
  | ImageWidgetConfig
  | NavWidgetConfig
  | PaymentMethodWidgetConfig
  | RadioWidgetConfig
  | SegmentWidgetConfig
  | SlotWidgetConfig
  | TagLineWidgetConfig
  | TextWidgetConfig

// ---
// Нужно поменять все key на name, поскольку в React проп key не прокидывается внутрь компонента
// ---

// Выборка конфигов без key. Их модифицировать не нужно
type WidgetConfigWithoutKey = Exclude<WidgetConfig, {key: string}>

// Выборка конфигов с key
type WidgetConfigWithKey = Extract<WidgetConfig, {key: string}>
// Меняем key на name
type WidgetConfigWithName = Omit<WidgetConfigWithKey, 'key'> & {name: string}

// Собираем конфиги обратно в union всех типов виджетов
export type WidgetProps = WidgetConfigWithName | WidgetConfigWithoutKey

/**
 * Утилита для получения пропов виджета по его типу
 */
export type WidgetPropsByType<T extends WidgetType> = Extract<WidgetProps, {type: T}>
